<template>
    <div class="overlayConfigPanel musicPanel">
        <div class="ocp-flyout">
            <div class="ocp-header">
                <p class="ot-title">{{ $t('dash.post-creator.media-editor.music-panel.title') }}</p>
                <Tooltip :text="$t('dash.post-creator.media-editor.overlay-config.close-title')" position="left">
                    <i class="fa-solid fa-xmark ocp-close" @click="$emit('close')"></i>
                </Tooltip>
            </div>

            <div class="ocp-body">
                <p class="ct-hint" v-if="!audioClips.length">{{ $t('dash.post-creator.media-editor.music-panel.empty-hint') }}</p>

                <div class="mp-track" v-for="clip in audioClips" :key="clip.id">
                    <div class="mp-track-header">
                        <i class="fa-solid fa-music"></i>
                        <span class="mp-track-name">{{ fileNames[clip.fileId] || $t('dash.post-creator.media-editor.music-panel.fallback-name') }}</span>
                        <i class="fa-solid fa-trash mp-remove" @click="$emit('remove-track', clip.id)"></i>
                    </div>

                    <div class="ot-row">
                        <label>{{ $t('dash.post-creator.media-editor.music-panel.volume') }}</label>
                        <input
                            type="range"
                            min="0"
                            max="1"
                            step="0.05"
                            :value="clip.volume"
                            @input="e => $emit('update-track', { clipId: clip.id, patch: { volume: Number(e.target.value) } })"
                        >
                    </div>

                    <!-- Which part of the source file this clip actually plays - independent of
                         where/how long it sits on the video's own timeline (that's now dragged
                         directly on the timeline itself, see AudioTrackRow.vue). Both handles
                         move freely within the source's own full length; changing the excerpt's
                         length this way (rather than just shifting it) resizes how much room it
                         takes on the timeline too (see onTrimRangeChange), so the two always stay
                         in sync - the timeline's own edge-drag and this slider are just two
                         different ways to reach the same trimStartMs/trimEndMs. -->
                    <div class="ot-row" v-if="clip.sourceDurationMs > usedDurationMs(clip)">
                        <label>{{ $t('dash.post-creator.media-editor.music-panel.trim-in-file') }}</label>
                    </div>
                    <div class="ct-trim-slider" v-if="clip.sourceDurationMs > usedDurationMs(clip)">
                        <VueSlider
                            :value="[clip.trimStartMs, clip.trimEndMs]"
                            :min="0"
                            :max="clip.sourceDurationMs"
                            :interval="100"
                            :enable-cross="false"
                            :dot-options="dotZIndexOptions([clip.trimStartMs, clip.trimEndMs], 0, clip.sourceDurationMs)"
                            :tooltip-formatter="formatTimecode"
                            tooltip="always"
                            :height="6"
                            :dot-size="16"
                            @change="range => onTrimRangeChange(clip, range)"
                        />
                    </div>

                    <Tooltip
                        v-if="clip.sourceDurationMs"
                        :text="$t(previewingClipId === clip.id ? 'dash.post-creator.media-editor.music-panel.stop-preview' : 'dash.post-creator.media-editor.music-panel.preview')"
                        classCustom="ot-reposition mp-preview"
                        @click="togglePreview(clip)"
                    >
                        <i class="fa-solid" :class="previewingClipId === clip.id ? 'fa-stop' : 'fa-play'"></i>
                        {{ $t(previewingClipId === clip.id ? 'dash.post-creator.media-editor.music-panel.stop-preview' : 'dash.post-creator.media-editor.music-panel.preview') }}
                    </Tooltip>
                </div>
            </div>

            <input ref="fileInput" type="file" accept="audio/*" hidden @change="onFilePicked">
            <div class="ot-reposition" @click="$refs.fileInput.click()">
                <i class="fa-solid fa-plus"></i> {{ $t('dash.post-creator.media-editor.music-panel.add') }}
            </div>
        </div>
    </div>
</template>

<script>
import VueSlider from 'vue-slider-component'
import 'vue-slider-component/theme/antd.css'
import Tooltip from '@/components/dash/tooltip/tooltipInit.vue'
import { formatTimecode } from '../engine/time.js'
import { dotZIndexOptions } from '../engine/sliderUtils.js'

export default {
    name: 'MusicPanel',

    components: {
        VueSlider,
        Tooltip
    },

    props: {
        audioClips: {
            type: Array,
            default: () => []
        },
        fileNames: {
            type: Object,
            default: () => ({})
        },
        sources: {
            type: Object,
            default: () => ({})
        }
    },

    data() {
        return {
            // Which clip (if any) is currently being auditioned - see togglePreview. One shared
            // <audio> element for the whole panel, since only one preview ever plays at a time.
            previewingClipId: null,
            previewClip: null
        }
    },

    beforeDestroy() {
        this.stopPreview()
    },

    methods: {
        formatTimecode,
        dotZIndexOptions,

        usedDurationMs(clip) {
            return clip.timelineEndMs - clip.timelineStartMs
        },

        // Keeps the clip's placement on the timeline in sync with whatever excerpt length the
        // dual slider now selects - its own start stays put, its end follows the new duration, so
        // dragging this slider and dragging the clip's own edge directly on the timeline (see
        // AudioTrackRow.vue) are just two paths to the exact same trimStartMs/trimEndMs pair
        // instead of silently drifting apart from each other.
        onTrimRangeChange(clip, range) {
            const [trimStartMs, trimEndMs] = range
            this.$emit('update-track', {
                clipId: clip.id,
                patch: { trimStartMs, trimEndMs, timelineEndMs: clip.timelineStartMs + (trimEndMs - trimStartMs) }
            })
        },

        togglePreview(clip) {
            if (this.previewingClipId === clip.id) {
                this.stopPreview()
                return
            }
            this.stopPreview()
            const url = this.sources[clip.fileId]
            if (!url) return

            if (!this.previewAudioEl) {
                this.previewAudioEl = new Audio()
                this.previewAudioEl.addEventListener('timeupdate', this.onPreviewTimeUpdate)
                this.previewAudioEl.addEventListener('ended', this.stopPreview)
            }
            this.previewAudioEl.src = url
            this.previewAudioEl.currentTime = clip.trimStartMs / 1000
            this.previewAudioEl.volume = clip.volume !== undefined ? clip.volume : 1
            this.previewAudioEl.play().catch(() => {})
            this.previewingClipId = clip.id
            this.previewClip = clip
        },

        // previewClip is the live clip object (not a snapshot) - dragging the slider while a
        // preview is playing updates trimEndMs immediately, so the auditioned excerpt always
        // stops exactly where the slider currently says it should, even mid-playback.
        onPreviewTimeUpdate() {
            if (!this.previewClip) return
            if (this.previewAudioEl.currentTime * 1000 >= this.previewClip.trimEndMs) this.stopPreview()
        },

        stopPreview() {
            if (this.previewAudioEl) this.previewAudioEl.pause()
            this.previewingClipId = null
            this.previewClip = null
        },

        onFilePicked(e) {
            const file = e.target.files[0]
            e.target.value = null
            if (!file) return
            this.$emit('add-track', file)
        }
    }
}
</script>

<style lang="scss" scoped></style>
